import styles from './ProjectPageContent.module.less'
import AIcon from '@/components/A/AIcon/AIcon'
import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { useInViewport } from 'ahooks'
import { useAutoRequest, usePagination } from 'alova/client'
import { api } from '@/api'
import { Spin, message } from 'antd'
import NiceModal from '@ebay/nice-modal-react'
import CommonModal from '@/components/Modals/CommonModal/CommonModal'
import { commonUtils } from '@/helpers/commonUtils'
import { useTranslation } from 'react-i18next'
import ProjectCard from '../ProjectCard/ProjectCard'
import { observer } from 'mobx-react'
import { ProjectFilterSort, ProjectSearchFilter } from '../ProjectFilter/ProjectFilter'
import useProductType from '@/helpers/commonHooks/useProductType'
import { isPortraitProductCategory } from '@/helpers/productCategory'

const PAGE_SIZE = 20

export const ProjectPageContext = createContext<
  | {
    handleDelete: (item: ProjectModels.ProjectListItem) => void
    handleRetry: (item: ProjectModels.ProjectListItem) => void
  }
  | undefined
>(undefined)

export const usePollingProgress = (projectIdList: number[]) => {
  const [startPollingProgress, setStartPollingProgress] = useState<boolean>(false)
  const { data, send } = useAutoRequest(api.project.projectProgress(projectIdList), {
    initialData: { data: {} },
    /** 因为后端是 30s 去查询一次状态，所以前端这里不用太频繁去轮询状态， */
    pollingTime: 5000,
    middleware: (_, next) => {
      if (startPollingProgress) {
        next()
      }
    },
  })
  useEffect(() => {
    if (projectIdList.length === 0) {
      setStartPollingProgress(false)
    } else {
      send()
    }
  }, [projectIdList])

  return { setStartPollingProgress, progressData: data ? data.data : {} }
}

const useListStatus = (total: number | undefined, hasNext: boolean) => {
  const [status, setStatus] = useState<'empty' | 'hasMore' | 'noMore'>('hasMore')

  useEffect(() => {
    if (total === undefined) {
      setStatus('hasMore')
      return
    }
    if (!total) {
      setStatus('empty')
      return
    }
    setStatus(hasNext ? 'hasMore' : 'noMore')
  }, [total, hasNext])

  return status
}

export const ProgressContext = createContext<{
  progressData:
  | Record<number | string, { process: number; status: ProjectModels.ProjectListItem['status'] }>
  | undefined
}>({
  progressData: {},
})

function ProjectPageContent(props: { isDetailMode: boolean, whichProjectDetail: ProjectModels.ProjectDetail | ProjectModels.ProjectDetailV3 | null }) {
  const { t } = useTranslation()
  const { productType } = useProductType()
  const isPortraitMode = isPortraitProductCategory(productType)
  const [creativeTypeFilter, setCreativeTypeFilter] = useState<ProjectModels.ListCreativeTypeFilter>('all')
  const [keyword, setKeyword] = useState('')
  const [order, setOrder] = useState<'score_desc' | 'score_asc'>()
  const [normType, setNormType] = useState<1 | 2>()
  const [tabCounts, setTabCounts] = useState({ all: 0, general: 0, splash: 0 })
  const [hasNext, setHasNext] = useState(false)
  const {
    data: listData,
    send: sendListRequest,
    abort: abortListRequest,
    update: updateListData,
    remove: removeListItem,
    reload: reloadList,
    replace: replaceListItem,
    page,
    total,
    loading,
    refresh: refreshList,
  } = usePagination(
    (pageNo, pageSize) => {
      const param: Parameters<typeof api.project.projectList>[0] = {
        pageNo,
        pageSize,
        taskType: isPortraitMode ? 'portrait' : 'landscape',
      }
      if (keyword) {
        param.keyword = keyword
      }
      if (order) {
        param.orderBy = order
      }
      if (normType) {
        param.norm = normType
      }
      if (isPortraitMode) {
        if (creativeTypeFilter === 'general') {
          param.durationCategory = 'ten_plus'
        } else if (creativeTypeFilter === 'splash') {
          param.durationCategory = 'five_to_ten'
        }
      }
      param.projectCategory = productType
      return api.project.projectList(param)
    },
    {
      initialPage: 1,
      initialPageSize: PAGE_SIZE,
      data: (res) => res.objects,
      total: (res) => {
        setHasNext(res.hasNext)
        setTabCounts({
          all: res.allCount ?? res.totalCount ?? 0,
          general: res.generateCount ?? 0,
          splash: res.splashCount ?? 0,
        })
        return res.totalCount
      },
      append: true,
      immediate: false,
      preloadNextPage: false,
      preloadPreviousPage: false,
    }
  )

  const listStatus = useListStatus(total, hasNext)

  const [analyzingProjectIdList, setAnalyzingProjectIdList] = useState<number[]>([])

  const { setStartPollingProgress, progressData } = usePollingProgress(analyzingProjectIdList)
  const showInitialLoading = page === 1 && loading && listData.length === 0

  const paginationSentinelRef = useRef<HTMLDivElement>(null)
  const [paginationSentinelInView] = useInViewport(paginationSentinelRef)
  const [productSwitchLoading, setProductSwitchLoading] = useState(false)
  const loadMoreLockRef = useRef(false)
  const lastRequestedPageRef = useRef(1)
  const filterInitializedRef = useRef(false)

  const resetLoadMoreState = useCallback(() => {
    loadMoreLockRef.current = false
    lastRequestedPageRef.current = 1
  }, [])

  useEffect(() => {
    let cancelled = false
    setProductSwitchLoading(true)
    sendListRequest(1, PAGE_SIZE).finally(() => {
      if (!cancelled) {
        setProductSwitchLoading(false)
      }
    })
    return () => {
      cancelled = true
      abortListRequest()
    }
  }, [productType])

  useEffect(() => {
    setAnalyzingProjectIdList([])
    resetLoadMoreState()
    setCreativeTypeFilter('all')
  }, [productType, resetLoadMoreState])

  useEffect(() => {
    if (!filterInitializedRef.current) {
      filterInitializedRef.current = true
      return
    }
    resetLoadMoreState()
    reloadList()
  }, [keyword, order, normType, creativeTypeFilter, reloadList, resetLoadMoreState])

  useEffect(() => {
    if (!loading) {
      loadMoreLockRef.current = false
    }
  }, [loading])

  useEffect(() => {
    if (props.whichProjectDetail) {
      const index = listData.findIndex((p) => p.id === props.whichProjectDetail!.id)
      const listItem = listData[index]
      if (index !== -1) {
        replaceListItem({ ...listItem, ...props.whichProjectDetail! }, index)
      }
    }
  }, [props.whichProjectDetail])

  useEffect(() => {
    if (!paginationSentinelInView) {
      return
    }
    if (productSwitchLoading || loading || listStatus !== 'hasMore') {
      return
    }
    const nextPage = lastRequestedPageRef.current + 1
    if (loadMoreLockRef.current || lastRequestedPageRef.current >= nextPage) {
      return
    }
    loadMoreLockRef.current = true
    lastRequestedPageRef.current = nextPage
    updateListData({ page: nextPage })
  }, [
    paginationSentinelInView,
    productSwitchLoading,
    loading,
    listStatus,
    listData.length,
    updateListData,
  ])

  useEffect(() => {
    if (props.isDetailMode) {
      setAnalyzingProjectIdList([])
      return
    }
    const analyzingProjectIdList = listData
      .filter((d) => d.status === 'creating' || d.status === 'analyzing' || d.status === 'waiting')
      .map((project) => project.id)
    setAnalyzingProjectIdList(analyzingProjectIdList)
    if (analyzingProjectIdList.length > 0) {
      setStartPollingProgress(true)
    }
  }, [listData, props.isDetailMode])

  useEffect(() => {
    if (!progressData) return
    const pageNumbersToRefresh = new Set<number>()
    for (const id in progressData) {
      const projectIndex = listData.findIndex((p) => p.id === Number(id))
      if (projectIndex === -1) continue
      const project = listData[projectIndex]
      const progress = progressData[id]
      const status = progress.status
      if (project.status !== status) {
        replaceListItem({ ...project, status }, projectIndex)
        if (status === 'completed' || status === 'failed') {
          pageNumbersToRefresh.add(Math.floor(projectIndex / PAGE_SIZE) + 1)
        }
      }
    }
    pageNumbersToRefresh.forEach((pageNo) => {
      refreshList(pageNo)
    })
  }, [listData, progressData, refreshList, replaceListItem])

  const handleDelete = useCallback((item: ProjectModels.ProjectListItem) => {
    NiceModal.show(CommonModal, {
      title: t('dashboard.projectPage.message.deleteTitle'),
      content: <div className={styles.deleteContent}>
        <p className={styles.deleteText}>{t('dashboard.projectPage.message.delete')}</p>
        {
          item.isFreeTrial && (
            <p className={styles.deleteText}>({t('dashboard.projectPage.message.delete1')})</p>
          )
        }
        {
          item.status === 'waiting' && (
            <p className={styles.deleteText}>({t('dashboard.projectPage.message.delete2')})</p>
          )
        }
      </div>,
      confirmText: t('dashboard.projectPage.delete'),
      cancelText: t('dashboard.projectPage.cancel'),
      onConfirm: () => {
        api.project
          .projectDelete(item.id)
          .send()
          .then(() => {
            removeListItem(item)
            message.success(t('dashboard.projectPage.message.deleteSuccess'))
          })
      },
    })
  }, [])

  const handleRetry = useCallback((item: ProjectModels.ProjectListItem) => {
    api.project
      .projectRetry(item.id)
      .send()
      .then(() => {
        const projectIndex = listData.findIndex((project) => project.id === item.id)
        if (projectIndex === -1) {
          return
        }
        replaceListItem({ ...listData[projectIndex], status: 'analyzing', errorMsg: '' }, projectIndex)
      })
      .catch((e) => {
        const error = e as ApiError
        if (error.statusCode === 40021) {
          message.error(t('dashboard.projectPage.message.analysisCountNotEnough'))
          return
        }
        message.error(t('dashboard.projectPage.message.analysisFailed'))
      })
  }, [listData, replaceListItem, t])

  return (
    <div className={styles.projectPage} data-testid='project-page'>
        <div className={styles.topBox} data-portrait={isPortraitMode}>
          {isPortraitMode ? (
            <div className={styles.portraitTabs}>
              {([
                { key: 'all', label: t('dashboard.projectPage.tabAllCreative', { count: tabCounts.all }) },
                { key: 'general', label: t('dashboard.projectPage.tabGeneralCreative', { count: tabCounts.general }) },
                { key: 'splash', label: t('dashboard.projectPage.tabSplashCreative', { count: tabCounts.splash }) },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  type='button'
                  data-testid={`project-creative-type-${tab.key}-tab`}
                  className={styles.portraitTab}
                  data-active={creativeTypeFilter === tab.key}
                  onClick={() => {
                    setCreativeTypeFilter(tab.key)
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ) : null}
          <p className={styles.total}>
            <AIcon type='icon-fill-global_video1' />
            <span style={{ marginLeft: 6 }}>{t('dashboard.projectPage.totalDesc', { total })}</span>
          </p>
          <div className={styles.actionBox}>
            <ProjectFilterSort
              normType={normType}
              order={order}
              onNormTypeChange={(value) => {
                setNormType(value)
              }}
              onSortChange={(value) => {
                setOrder(value)
              }}
            />
            <ProjectSearchFilter onSearchChange={(value) => {
              const keyword = value ? `${value}` : ''
              setKeyword(keyword)
            }} />
          </div>
        </div>
        <ProjectPageContext.Provider value={{ handleDelete, handleRetry }}>
          <div className={styles.contentBox}>
            {showInitialLoading || productSwitchLoading ? (
              <div className={styles.loadingBox}>
                <Spin />
              </div>
            ) : (
              <>
                {listStatus === 'empty' && (
                  <div className={styles.emptyBox}>
                    <img
                      src={commonUtils.getImagePath('projectPage/project-list-empty.png')}
                      alt=''
                    />
                    <p>{t('dashboard.projectPage.noRelevantContent')}</p>
                  </div>
                )}
                {listStatus !== 'empty' && (
                  <>
                    <ProgressContext.Provider value={{ progressData }}>
                      <ProjectCardList projectItemList={listData} isPortrait={isPortraitMode} />
                    </ProgressContext.Provider>
                    {listStatus === 'hasMore' && listData.length > 0 ? (
                      <div className={styles.loadingBox} ref={paginationSentinelRef}>
                        {loading && page > 1 ? <Spin /> : null}
                      </div>
                    ) : null}
                  </>
                )}
              </>
            )}
          </div>
        </ProjectPageContext.Provider>
      </div>
  )
}

const ProjectCardList = (props: { projectItemList: ProjectModels.ProjectListItem[]; isPortrait: boolean }) => {
  return (
    <div className={styles.cardList} data-portrait={props.isPortrait} data-testid='project-card-list'>
      {props.projectItemList.map((item, index) => {
        return (
          <div className={styles.cardItem} key={item.id} data-testid={`project-card-item-${item.id}`}>
            <ProjectCard item={item} prioritizeCover={index < 6} isPortrait={props.isPortrait} />
            {/**空提交  test version 2*/}
          </div>
        )
      })}
    </div>
  )
}

export default observer(ProjectPageContent)
